import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, Mail } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import api, { formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteLayout branch="main">
      <div className="bg-[var(--cream)] py-16 sm:py-24 px-4">
        <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <span className="eyebrow-dark">RESET PASSWORD</span>
          <h1 className="font-display text-4xl sm:text-5xl text-[var(--navy)] mt-3 leading-tight">Forgot your password?</h1>
          <p className="text-[var(--muted)] mt-3 text-base leading-relaxed">
            Enter your email and we'll send you a link to choose a new one.
          </p>
        </div>

        {sent ? (
          <div className="bg-white border border-[var(--line)] p-10 text-center shadow-sm" data-testid="forgot-sent">
            <CheckCircle2 className="mx-auto text-green-600" size={44} />
            <h2 className="font-display text-2xl text-[var(--navy)] mt-5">Check your inbox.</h2>
            <p className="text-[var(--muted)] text-sm mt-3 leading-relaxed">
              If <strong className="text-[var(--navy)]">{email}</strong> matches an account, a reset link
              is on its way. The link expires in 60 minutes.
            </p>
            <Link to="/login" className="btn btn-navy mt-8 inline-flex">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[var(--line)] p-8 sm:p-10 space-y-5 shadow-sm" data-testid="forgot-form" noValidate>
            <div>
              <label htmlFor="forgot-email" className="block text-sm font-semibold text-[var(--navy)] mb-2">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" aria-hidden="true" />
                <input id="forgot-email" required type="email" autoComplete="email" autoFocus placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email"
                  className="w-full pl-10 pr-3 py-3 border border-[var(--line)] bg-[#fafafa] text-[15px] text-[var(--ink)] placeholder:text-neutral-400 focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors" />
              </div>
            </div>
            {err && (
              <div role="alert" className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-800 text-sm p-3.5" data-testid="forgot-error">
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="leading-relaxed">{err}</span>
              </div>
            )}
            <button type="submit" disabled={loading} className="btn btn-navy w-full justify-center h-12 text-[15px] disabled:opacity-60 disabled:cursor-not-allowed" data-testid="forgot-submit">
              {loading ? (<><Loader2 className="animate-spin mr-2" size={16} /> Sending…</>) : "Send reset link"}
            </button>
            <p className="text-center text-sm text-[var(--muted)] pt-1">
              Remembered it?{" "}
              <Link to="/login" className="text-[var(--navy)] font-bold hover:text-[var(--gold)] underline underline-offset-2">Sign in</Link>
            </p>
          </form>
        )}
        </div>
      </div>
    </SiteLayout>
  );
}
